import { useCallback, useEffect, useMemo, useState } from "react";

import type { YearBounds } from "./mapHelpers";

// Ported verbatim from V1 `~/models/entities/components/views/useMapDateRange` (pure).

const STORAGE_KEY = "dewey.v2.mapView.dateRange";

const loadSavedRange = (): [number, number] | null => {
  if (typeof window === "undefined") return null;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as [number, number];
      if (
        Array.isArray(parsed) &&
        parsed.length === 2 &&
        typeof parsed[0] === "number" &&
        typeof parsed[1] === "number"
      ) {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const useMapDateRange = (
  yearBounds: YearBounds,
  initialRange?: [number, number],
): {
  yearRange: [number, number] | null;
  setYearRange: (range: [number, number]) => void;
} => {
  const [userRange, setUserRange] = useState<[number, number] | null>(() => initialRange ?? loadSavedRange());

  const yearRange = useMemo<[number, number] | null>(() => {
    if (!yearBounds) return null;
    if (!userRange) return [yearBounds.min, yearBounds.max];
    if (userRange[1] < yearBounds.min || userRange[0] > yearBounds.max) {
      return [yearBounds.min, yearBounds.max];
    }
    return [Math.max(userRange[0], yearBounds.min), Math.min(userRange[1], yearBounds.max)];
  }, [yearBounds, userRange]);

  useEffect(() => {
    if (!yearRange) return;
    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(yearRange));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [yearRange]);

  const setYearRange = useCallback((range: [number, number]) => {
    setUserRange(range);
  }, []);

  return { yearRange, setYearRange };
};
